/* eslint-disable jsx-a11y/anchor-is-valid */
import {useState, useEffect} from 'react'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import clsx from 'clsx'
import {Link, useSearchParams, useNavigate} from 'react-router-dom'
import axios from 'axios'
import {PasswordMeterComponent} from '../../../../_metronic/assets/ts/components'
import {Toast} from '../../../../_metronic/partials/widgets'
import {AuthAuroraShell, AuthBrandHeader} from './AuthAurora'

const getInitialValues = (email?: string) => ({
  name: '',
  email: email || '',
  password: '',
  changepassword: '',
})

const registrationSchema = Yup.object().shape({
  name: Yup.string()
    .min(3, 'Mínimo de 3 caracteres')
    .max(100, 'Máximo de 100 caracteres')
    .required('Nome é obrigatório'),
  email: Yup.string()
    .email('Formato de email inválido')
    .min(3, 'Mínimo de 3 caracteres')
    .max(100, 'Máximo de 100 caracteres')
    .required('Email é obrigatório'),
  password: Yup.string()
    .min(8, 'Mínimo de 8 caracteres')
    .max(50, 'Máximo de 50 caracteres')
    .required('Senha é obrigatória'),
  changepassword: Yup.string()
    .required('Confirmação de senha é obrigatória')
    .when('password', {
      is: (val: string) => (val && val.length > 0 ? true : false),
      then: Yup.string().oneOf([Yup.ref('password')], 'As senhas não coincidem'),
    }),
})

interface DecodedParams {
  client_id: number
  instance_id: number
  project_id: number
  email?: string
}

export function ClientPortalUserRegistration() {
  const [loading, setLoading] = useState(false)
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [decodedParams, setDecodedParams] = useState<DecodedParams | null>(null)
  const [hasValidParams, setHasValidParams] = useState(false)
  const [isChecking, setIsChecking] = useState(true)
  const [invitedEmail, setInvitedEmail] = useState<string | undefined>(undefined)

  // Decodificar parâmetro da URL em base64
  useEffect(() => {
    const encodedParam = searchParams.get('data')

    if (!encodedParam) {
      // Sem parâmetro, redirecionar para login
      setIsChecking(false)
      setTimeout(() => {
        window.location.href = '/auth/area-do-cliente/login'
      }, 100)
      return
    }

    try {
      // Decodificar base64
      const decodedString = atob(encodedParam)
      const params = JSON.parse(decodedString) as DecodedParams

      if (params.client_id && params.instance_id && params.project_id) {
        setDecodedParams(params)
        if (params.email) {
          setInvitedEmail(params.email)
        }
        setHasValidParams(true)
        setIsChecking(false)
      } else {
        // Parâmetros inválidos, redirecionar para login
        setIsChecking(false)
        setTimeout(() => {
          window.location.href = '/auth/area-do-cliente/login'
        }, 100)
      }
    } catch (error) {
      console.error('Erro ao decodificar parâmetros:', error)
      // Erro ao decodificar, redirecionar para login
      setIsChecking(false)
      setTimeout(() => {
        window.location.href = '/auth/area-do-cliente/login'
      }, 100)
    }
  }, [searchParams])

  const formik = useFormik({
    initialValues: getInitialValues(invitedEmail),
    enableReinitialize: true,
    validationSchema: registrationSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      if (!hasValidParams || !decodedParams) {
        setStatus('Parâmetros inválidos. Não é possível realizar o cadastro.')
        return
      }

      setLoading(true)
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000'

        await axios.post(`${apiUrl}/api/client-portal-users/register`, {
          name: values.name,
          email: values.email,
          password: values.password,
          client_id: decodedParams.client_id,
          instance_id: decodedParams.instance_id,
          project_id: decodedParams.project_id,
        })

        Toast.fire({
          icon: 'success',
          title: 'Cadastro realizado com sucesso!',
          text: 'Sua conta foi criada. Você pode fazer login agora.',
          customClass: {
            popup: 'bg-light-success',
            title: 'text-success',
          },
        })

        // Redirecionar para login após 2 segundos
        setTimeout(() => {
          window.location.href = '/auth/area-do-cliente/login'
        }, 2000)
      } catch (error: any) {
        console.error(error)
        const errorMessage =
          error.response?.data?.message ||
          error.response?.data?.error ||
          'Erro ao realizar cadastro. Tente novamente.'
        setStatus(errorMessage)
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  useEffect(() => {
    if (hasValidParams) {
      PasswordMeterComponent.bootstrap()
    }
  }, [hasValidParams])

  // Não renderizar se ainda estiver verificando ou se não houver parâmetros válidos
  if (isChecking || !hasValidParams || !decodedParams) {
    return null
  }

  return (
    <AuthAuroraShell>
      <AuthBrandHeader badge='Portal do Cliente' />

      <form
        className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
        noValidate
        id='kt_client_portal_registration_form'
        onSubmit={formik.handleSubmit}
      >
        {/* begin::Heading */}
        <div className='text-center mb-8'>
          <h1 className='text-dark fw-bolder mb-3'>Cadastro</h1>
          <div className='text-gray-500 fw-semibold fs-6'>
            Preencha os dados para criar sua conta
          </div>
        </div>
        {/* end::Heading */}

        {formik.status && (
          <div className='mb-lg-15 alert alert-danger'>
            <div className='alert-text font-weight-bold'>{formik.status}</div>
          </div>
        )}

        {/* begin::Form group Name */}
        <div className='fv-row mb-8'>
          <label className='form-label fw-bolder text-dark fs-6'>Nome completo</label>
          <input
            placeholder='Nome completo'
            type='text'
            autoComplete='off'
            {...formik.getFieldProps('name')}
            className={clsx(
              'form-control bg-transparent',
              {
                'is-invalid': formik.touched.name && formik.errors.name,
              },
              {
                'is-valid': formik.touched.name && !formik.errors.name,
              }
            )}
            disabled={!hasValidParams}
          />
          {formik.touched.name && formik.errors.name && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{formik.errors.name}</span>
              </div>
            </div>
          )}
        </div>
        {/* end::Form group */}

        {/* begin::Form group Email */}
        <div className='fv-row mb-8'>
          <label className='form-label fw-bolder text-dark fs-6'>Email</label>
          <input
            placeholder='Email'
            type='email'
            autoComplete='off'
            {...formik.getFieldProps('email')}
            className={clsx(
              'form-control bg-transparent',
              {'is-invalid': formik.touched.email && formik.errors.email},
              {
                'is-valid': formik.touched.email && !formik.errors.email,
              }
            )}
            disabled={!hasValidParams || !!invitedEmail}
          />
          {invitedEmail && (
            <div className='form-text text-muted'>
              <i className='bi bi-info-circle me-1'></i>
              Este email foi definido no convite e não pode ser alterado.
            </div>
          )}
          {formik.touched.email && formik.errors.email && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{formik.errors.email}</span>
              </div>
            </div>
          )}
        </div>
        {/* end::Form group */}

        {/* begin::Form group Password */}
        <div className='fv-row mb-8' data-kt-password-meter='true'>
          <div className='mb-1'>
            <label className='form-label fw-bolder text-dark fs-6'>Senha</label>
            <div className='position-relative mb-3'>
              <input
                type='password'
                placeholder='Senha'
                autoComplete='off'
                {...formik.getFieldProps('password')}
                className={clsx(
                  'form-control bg-transparent',
                  {
                    'is-invalid': formik.touched.password && formik.errors.password,
                  },
                  {
                    'is-valid': formik.touched.password && !formik.errors.password,
                  }
                )}
                disabled={!hasValidParams}
              />
              {formik.touched.password && formik.errors.password && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{formik.errors.password}</span>
                  </div>
                </div>
              )}
            </div>
            {/* begin::Meter */}
            <div
              className='d-flex align-items-center mb-3'
              data-kt-password-meter-control='highlight'
            >
              <div className='flex-grow-1 bg-secondary bg-active-success rounded h-5px me-2'></div>
              <div className='flex-grow-1 bg-secondary bg-active-success rounded h-5px me-2'></div>
              <div className='flex-grow-1 bg-secondary bg-active-success rounded h-5px me-2'></div>
              <div className='flex-grow-1 bg-secondary bg-active-success rounded h-5px'></div>
            </div>
            {/* end::Meter */}
          </div>
          <div className='text-muted'>
            Use 8 ou mais caracteres com uma mistura de letras, números e símbolos.
          </div>
        </div>
        {/* end::Form group */}

        {/* begin::Form group Confirm password */}
        <div className='fv-row mb-5'>
          <label className='form-label fw-bolder text-dark fs-6'>Confirmar Senha</label>
          <input
            type='password'
            placeholder='Confirmação de senha'
            autoComplete='off'
            {...formik.getFieldProps('changepassword')}
            className={clsx(
              'form-control bg-transparent',
              {
                'is-invalid': formik.touched.changepassword && formik.errors.changepassword,
              },
              {
                'is-valid': formik.touched.changepassword && !formik.errors.changepassword,
              }
            )}
            disabled={!hasValidParams}
          />
          {formik.touched.changepassword && formik.errors.changepassword && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{formik.errors.changepassword}</span>
              </div>
            </div>
          )}
        </div>
        {/* end::Form group */}

        {/* begin::Form group */}
        <div className='text-center'>
          <button
            type='submit'
            id='kt_client_portal_registration_submit'
            className='btn btn-lg btn-primary w-100 mb-5'
            disabled={formik.isSubmitting || !formik.isValid || !hasValidParams}
          >
            {!loading && <span className='indicator-label'>Cadastrar</span>}
            {loading && (
              <span className='indicator-progress' style={{display: 'block'}}>
                Aguarde...{' '}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
        {/* end::Form group */}
      </form>

      <p className='tr-auth-security-note text-center mt-6 mb-0'>
        <i className='bi bi-shield-check me-2'></i>
        Esta é uma área segura para cadastro no Portal do Cliente
      </p>
    </AuthAuroraShell>
  )
}
